import {
  $applyNodeReplacement,
  DOMConversionMap,
  NodeKey,
  SerializedTextNode,
  Spread,
  TextNode,
} from 'lexical';

export type SerializedThreadAwareTextNode = Spread<
  {
    threadId?: string;
  },
  SerializedTextNode
>;

export class ThreadAwareTextNode extends TextNode {
  __threadId?: string;

  static getType(): string {
    return 'thread-aware-text';
  }

  static importDOM(): DOMConversionMap | null {
    return null;
  }

  static clone(node: ThreadAwareTextNode): ThreadAwareTextNode {
    return new ThreadAwareTextNode(node.__text, node.__threadId, node.__key);
  }

  constructor(text: string, threadId?: string, key?: NodeKey) {
    super(text, key);
    this.__threadId = threadId;
  }

  createDOM(config: any): HTMLElement {
    const element = super.createDOM(config);

    // Add thread ID as data attribute for styling and identification
    if (this.__threadId) {
      element.setAttribute('data-thread-id', this.__threadId);
      element.classList.add('thread-anchored');
    }

    return element;
  }

  updateDOM(prevNode: ThreadAwareTextNode, dom: HTMLElement, config: any): boolean {
    const isUpdated = super.updateDOM(prevNode as this, dom, config);

    // Update thread ID attributes
    if (prevNode.__threadId !== this.__threadId) {
      if (this.__threadId) {
        dom.setAttribute('data-thread-id', this.__threadId);
        dom.classList.add('thread-anchored');
      } else {
        dom.removeAttribute('data-thread-id');
        dom.classList.remove('thread-anchored');
      }
      return true;
    }

    return isUpdated;
  }

  setThreadId(threadId: string | undefined): void {
    const writableNode = this.getWritable();
    writableNode.__threadId = threadId;
  }

  getThreadId(): string | undefined {
    return this.getLatest().__threadId;
  }

  exportJSON(): SerializedThreadAwareTextNode {
    return {
      ...super.exportJSON(),
      threadId: this.__threadId,
      type: 'thread-aware-text',
      version: 1,
    };
  }

  static importJSON(serializedNode: SerializedThreadAwareTextNode): ThreadAwareTextNode {
    const {text, threadId} = serializedNode;
    const node = $createThreadAwareTextNode(text);
    if (threadId) {
      node.setThreadId(threadId);
    }
    return node;
  }

  // Override split to maintain thread ID in both parts
  splitText(...splitOffsets: number[]): TextNode[] {
    const parts = super.splitText(...splitOffsets);

    // If this node(has a)thread ID, preserve it in all parts
    if (this.__threadId) {
      parts.forEach((part) => {
        if (part instanceof ThreadAwareTextNode) {
          part.setThreadId(this.__threadId);
        }
      });
    }

    return parts;
  }

  // Override merge to handle thread ID conflicts
  mergeWithSibling(target: TextNode): TextNode {
    const merged = super.mergeWithSibling(target);

    // If target is thread-aware and has a thread ID, preserve it
    if (target instanceof ThreadAwareTextNode && target.__threadId) {
      if (merged instanceof ThreadAwareTextNode) {
        merged.setThreadId(target.__threadId);
      }
    }

    return merged;
  }
}

export function $createThreadAwareTextNode(text: string = ''): ThreadAwareTextNode {
  return $applyNodeReplacement(new ThreadAwareTextNode(text));
}

export function $isThreadAwareTextNode(node: unknown): node is ThreadAwareTextNode {
  return node instanceof ThreadAwareTextNode;
}
